import QuillEditor, { IQuillEditorWrapper } from "../components/universal/quill-editor"
import { Component, For, Match, Show,  Switch, createSignal, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { Progress } from "../components/progress"
import { A } from "../components/universal/a-href"
import { Required } from "../components/universal/required-icon";
import { Img } from "../components/universal/image";
import ImageCropper, { ImageFileData } from "../components/universal/image-cropper";
import { EntrantProfile, KeyOfType,  Step1RouteData } from "../types";
import { useNavigate, useRouteData } from "@solidjs/router";
import { commitChange, fetchPost, scrollToTop } from "../library/common";
import { ErrorMessage, useForm } from "../components/universal/validation";
import { Button, Form } from "solid-bootstrap"
import toast from "solid-toast"
import { getApplicationStatusJson, getText, loadStep1Cms } from "../app"
import { CMS } from "../components/cms/cms"
import { PageLoading } from "../components/page-loading"

type State = {
    isSubmitting: boolean;
    isContentReady: boolean;
    showImageCropper: boolean;
}

export function Step1Page () {
    const [state, setState] = createStore<State>({ isSubmitting: false, isContentReady: false, showImageCropper: false } as State);
    const navigate = useNavigate();
    const required = ({ value }: any) => !value && getText("error::please-enter-a-value", "Please enter a value");
    const maxLength = (el: HTMLInputElement, max: number) => {
        const len = el.value?.length;
        return len > max && `The field must be no longer than ${max} characters long. Please remove ${len - max} characters.`
    };
    const requiredListItem = ({ value }: any) => {
        return (!value || value == "null") && getText("error::please-select-from-the-list", "Please select from the list");
    }
    const conditionallyRequired = (condition: boolean, value: string) => condition && !value && getText("error::please-enter-a-value", "Please enter a value");
    const mustAgreeToTermsAndConditions = ({ value }: any) => !value && getText("18th-salon-application/step-1::must-accept-terms-and-conditions-error", "You must accept the Terms and Conditions before you can proceed.");
    const { formSubmit, validate, errors } = useForm({
        errorClass: "error-input"
    });
    const [quillInstance, setQuillInstance] = createSignal<EntrantProfile>();    
    const [profileImageUrl, setProfileImageUrl] = createSignal<string>("");
    const [cropper, setCropper] = createSignal<any>();
    const [biography, setBiography] = createSignal<string>("");
    const [countries, setCountries] = createSignal<any[]>([]);
    const [salon, setSalon] = createSignal<any>();
    const [model, setModel] = createStore({
        biography: "",
        personal: {
            firstName: "",
            lastName: ""
        },
        profile: {
            imageUrl: ""
        },
        address: {
            streetAddress1: "",
            streetAddress2: "",
            city: "",
            state: "",
            countryId: "",
            postalCode: ""
        },
        contact: {
            email: "",
            phoneNumber: ""
        },
        socials: {
            twitter: "",
            facebook: "",
            instagram: ""
        },
        isStudent: false,
        student: {
            school: {
                name: "",
                instructors: "",
                isTitle1: false,
                claimsIsTitle1: false
            },
            parentOrLegalGuardian: {
                name: "",
                email: "",
                phoneNumber: ""
            }
        },
        acceptedTermsAndConditions: false,
        catalogue: {
            wantsAdditional: false,
            additionalOrdered: 0,
            wantsFree: true
        }
    } as EntrantProfile);
    const sessionGuid = (new Date()).getTime();
    // @ts-expect-error: Not assignable…
    const setModelWithEffect: SetStoreFunction<EntrantProfile> = (...params: [any]) => {
        /**
         * https://stackoverflow.com/questions/78209469/how-to-create-a-wrapper-for-solidjss-setstore-in-typescript
         */
        setModel(...params);
        commitChange("17th-arc-salon-application/step-1/model-change", {
            Session: sessionGuid, Id: model.id, Values: params
        }).then();
    };
    const ModelEditor : Component<{ field: keyof KeyOfType<EntrantProfile, string>; editor: keyof KeyOfType<EntrantProfile, IQuillEditorWrapper>; label?: string; }> = (props) =>
    <>
        <label>{props.label || props.field}</label>
        <QuillEditor content={model[props.field] as string} setInstance={(editor) => setModel(props.editor, editor) } />
    </>;
    const showWantsAdditionalCataloguesPanel = () => model.catalogue.wantsFree && model.catalogue.wantsAdditional;
    const isValid = async () => {
        if (!model.acceptedTermsAndConditions) {
            await commitError(getText("18th-salon-application/step-1::must-accept-terms-and-conditions-error", "You must accept the Terms and Conditions before you can proceed."));
            return false;
        }
        if (model.biographyEditor?.isEmpty()) {
            await commitError(getText("18th-salon-application/step-1::biography-required-error", "You must provide a short biography."));
            return false;
        }
        return true;
    }
    const onChangeImageClicked = (e: Event) => {
        e.preventDefault();
        setState("showImageCropper", true);
    }
    const onKeepImageClicked = (e: Event) => {
        e.preventDefault();
        setState("showImageCropper", false);
    }
    const getModel = () => {
        return {
            Id: model.id,

            FirstName: model.personal.firstName,
            LastName: model.personal.lastName,

            Email: model.contact.email,
            PhoneNumber: model.contact.phoneNumber,

            StreetAddress1: model.address.streetAddress1,
            StreetAddress2: model.address.streetAddress2,
            City: model.address.city,
            State: model.address.state,
            CountryId: model.address.countryId,
            PostalCode: model.address.postalCode,

            Twitter: model.socials.twitter,
            Facebook: model.socials.facebook,
            Instagram: model.socials.instagram,

            Biography: model.biographyEditor?.getHtml(),

            WantsFreeCatalogue: model.catalogue.wantsFree,
            AdditionalCataloguesOrdered: model.catalogue.additionalOrdered || "",
            WantsAdditionalCatalogues: model.catalogue.wantsAdditional,

            IsHighSchoolStudent: model.isStudent,
            HighSchool: model.student.school.name,
            HighSchoolInstructors: model.student.school.instructors,
            Age: model.student.age,
            ClaimsToAttendTitle1HighSchool: model.student.school.claimsIsTitle1,

            ParentOrLegalGuardianName: model.student.parentOrLegalGuardian.name,
            ParentOrLegalGuardianEmail: model.student.parentOrLegalGuardian.email,
            ParentOrLegalGuardianPhoneNumber: model.student.parentOrLegalGuardian.phoneNumber,

            AcceptedTermsAndConditions: model.acceptedTermsAndConditions,
        };
    };
    const getErrors = (obj: any) => Object.keys(obj).filter(key => obj[key].length);
    const saveProfile = async () => {
        try {
            const model = getModel();
            const response = await fetchPost("/SalonApplication/Salon/Step1", model);
            const json = await response.json();
            if (!json.Success) {
                setState({ isSubmitting: false });
                return {
                    success: false,
                    errors: json.Errors
                };
            }
            return {
                success: true,
                salonEntrantId: json.Data.SalonEntrantId
            };
        } catch (ex: any) {
            setState({ isSubmitting: false });
            await commitError("[006] There was an error saving or updating your profile data.", ex);
            return {
                 success: false,
                 errors: [ex.Message]
            }
        }
    };
    const commitError = async (error: string, ex?: unknown) => {
        await commitChange("17th-arc-salon-application/step-1/error", {
            Error: error,
            Exception: ex?.toString()
        });
        toast.error(error);
    }
    const validateProfileImage = async () => {
        
        const imageFile = (await cropper().getImageFile()) as ImageFileData;
        if (!imageFile) {
            await commitError("[000] " + getText("18th-salon-application/step-1::please-attach-profile-image-error", "Please attach a profile image."));
            return false;
        }
        if (!imageFile.isValid) {
            await commitError("[001] " + getText("18th-salon-application/step-1::issue-with-profile-image-file-format-error", "There is an issue with the profile image or its file format. Please ensure it is a JPG or PNG file within the required dimensions."));
            return false;
        }
        
        if (!imageFile) {
            await commitError("[002] " + getText("18th-salon-application/step-1::attach-profile-image-error", "Please attach a profile image."));
            return false;
        }
        if (!imageFile.isValid) {
            await commitError("[003] " + getText("18th-salon-application/step-1::profile-image-invalid-error", "The profile image file is not valid. Please check the format and try again."));
            return false;
        }
        return true;
    }
    const saveProfileImage = async () => {
        try {
            const imageFile = (await cropper().getImageFile()) as ImageFileData;
            await commitChange("17th-arc-salon-application/step-1/profile-image", {
                OriginalFilename: imageFile.filename,
                FileSize: imageFile?.image?.size,
                Errors: imageFile?.errors,
                IsValid: imageFile?.isValid,
                IsDirty: imageFile?.isDirty
            });
            if (imageFile.isDirty){
                const attachingId = toast("Attaching the profile image to your application ...")
                const data = {
                    SalonEntrantId: model.id,
                    SalonEntrantImageId: model.profile.salonEntrantImageId,
                    ImageFile: imageFile.image,
                    OriginalFilename: imageFile.filename
                };
                const response = await fetchPost("/SalonApplication/Salon/UploadArtistPhotoStreamingJson", data);
                const json = await response.json();
                if (!json.Success) {
                    return await commitError("[004] " + getText("18th-salon-application/step-1::profile-image-upload-error", "There was an error uploading the profile image. Please try again."))
                }
                toast.remove(attachingId);
            }
        } catch (ex: any) {
            setState({ isSubmitting: false });
            await commitError("[004.1] " + getText("18th-salon-application/step-1::profile-image-upload-error", "There was an error uploading the profile image. Please try again."), ex)
        }
    };
    const goToStep2 = () => {
        toast.success(getText("18th-salon-application/step-1::profile-saved", "Your application profile has been saved."));
        navigate("/step-2");
    };
    const save = async () => {
        setState({ isSubmitting: true });
        await commitChange("17th-arc-salon-application/step-1/form", getModel());
        const checkingFormId = toast("Checking the form for errors ...");
        const isFormValid = await isValid();
        toast.remove(checkingFormId);
        if (!isFormValid) {
            setState({ isSubmitting: false });
            return;
        }
        const result = await saveProfile();
        if (!result.success) {
            setState({ isSubmitting: false });
            return await commitError("[005] " + getText("18th-salon-application/step-1::check-form-for-errors", "Please check the form for errors") + ": " + getErrors(result.errors).join("; "));
        }
        setModelWithEffect("id", result.salonEntrantId);
        if (state.showImageCropper) {
            const checkingImageId = toast("Checking the profile image for errors ...");
            const isValidImage = await validateProfileImage();
            if (!isValidImage) {
                setState({ isSubmitting: false });
                toast.remove(checkingImageId);
                return;
            }
            toast.remove(checkingImageId);
            await saveProfileImage();
        }
        await getApplicationStatusJson({ forceRefresh: true });
        setState({ isSubmitting: false });
        goToStep2();
    };
    const handleSubmit = async () => {
        await save();
    };
    onMount(async () => {
        const routeData = await useRouteData() as Step1RouteData;
        await loadStep1Cms();
        setCountries(routeData.resources.countries);
        setSalon(routeData.resources.salon);
        setModel(routeData.step1);
        if (!model.catalogue.wantsFree) {
            setModel("catalogue", "wantsAdditional", false);
            setModel("catalogue", "additionalOrdered", 1);
        }
        const hasProfileImage = !!routeData.step1.profile?.imageUrl;
        if (hasProfileImage) {
            setProfileImageUrl(routeData.step1.profile.imageUrl);            
        }
        setState("showImageCropper", !hasProfileImage);
        setState({ isContentReady: true });
        scrollToTop();
    });
    return (
        <Show when={state.isContentReady} fallback={<PageLoading />}>
            <div style="font-family: 'Bariol-Regular'; font-size: 1.25em;">
                <Progress step={1} />
                <h4 class="salon-heading-md" style="text-align: center;">
                    {salon()?.name} Application
                </h4>
                <form use:formSubmit={handleSubmit}>
                    <h4 class="salon-heading-md m-b-lg" style="margin-top: 20px;">
                        <CMS id="18th-salon-application/step-1::your-details/heading" text>Your Details</CMS>
                    </h4>
                    <CMS id="18th-salon-application/step-1::your-details/description">
                        <p style="font-size: 1.2em;">
                            You only need to register for the ARC Salon once; to manage your artworks on future visits, just go straight to the <b>LOG IN</b> page.
                        </p>
                        <p style="padding-top: 20px;">
                            If you experience any difficulties, please contact our webmaster for assistance at <A class="tape" href="mailto:iian.d.neill@gmail.com">iian.d.neill@gmail.com</A> with a description of the problem you encountered.
                        </p>
                        <p>
                            Fields that are marked with a <span class="fa fa-star" style="color: red;"></span> are required.
                        </p>
                    </CMS>
                    <hr />
                    <div class="row m-t-lg">
                        <div class="col-md-12">
                            <CMS id="18th-salon-application/step-1::section/name">
                                <h3 class="m-t-lg">
                                    Name
                                </h3>
                                <p>
                                    This is the name by which you'd like to be known in the ARC Salon competition.
                                </p>
                            </CMS>
                            <div class="row">
                                <div class="col-md-3">
                                    <label>
                                        <Required /> <CMS id="label::first-name" text>First name</CMS>
                                    </label>
                                    <input type="text"
                                        value={model.personal.firstName}
                                        name="personal_firstName"
                                        onInput={(e) => setModel("personal", "firstName", e.currentTarget?.value)}
                                        onChange={(e) => setModelWithEffect("personal", "firstName", e.currentTarget?.value)}
                                        use:validate={[required, (el: any) => maxLength(el, 30)]}
                                        class="form-control"
                                    />
                                    <ErrorMessage error={errors.personal_firstName} />
                                </div>
                                <div class="col-md-3">
                                    <label>
                                        <Required /> <CMS id="label::last-name" text>Last name</CMS>
                                    </label>
                                    <input type="text"
                                        value={model.personal.lastName}
                                        onInput={(e) => setModel("personal", "lastName", e.currentTarget?.value)}
                                        onChange={(e) => setModelWithEffect("personal", "lastName", e.currentTarget?.value)}
                                        name="personal_lastName"
                                        use:validate={[required, (el: any) => maxLength(el, 30)]}
                                        class="form-control"
                                    />
                                    <ErrorMessage error={errors.personal_lastName} />
                                </div>
                            </div>
                            <hr/>
                            <CMS id="18th-salon-application/step-1::section/contact-details">
                                <h3 class="m-t-lg">
                                    Contact details
                                </h3>
                                <p>
                                    ARC will not share your contact details without your permission.
                                </p>
                            </CMS>
                            <div class="row">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::email" text>Email</CMS>
                                    </label>
                                    <input
                                        type="email"
                                        value={model.contact.email}
                                        onChange={(e) => setModelWithEffect("contact","email", e.currentTarget?.value)}
                                        use:validate={[required, (el: any) => maxLength(el, 255)]}
                                        name="contact_email"
                                        class="form-control" />
                                    <ErrorMessage error={errors.contact_email} />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::street-line-1" text>Street (line 1)</CMS>
                                    </label>
                                    <input type="text"
                                        value={model.address.streetAddress1}
                                        name="address_streetAddress1"
                                        onChange={(e) => setModelWithEffect("address","streetAddress1", e.currentTarget?.value)}
                                        use:validate={[required, (el: any) => maxLength(el, 100)]}
                                        class="form-control" />
                                    <ErrorMessage error={errors.address_streetAddress1} />
                                </div>
                                <div class="col-md-6">
                                    <label>
                                        <CMS id="label::street-line-2" text>Street (line 2)</CMS>
                                    </label>
                                    <input
                                        value={model.address.streetAddress2}
                                        onChange={(e) => setModelWithEffect("address","streetAddress2", e.currentTarget?.value)}
                                        name="address_streetAddress2"
                                        class="form-control"
                                        use:validate={[(el: any) => maxLength(el, 100)]}
                                    />
                                    <ErrorMessage error={errors.address_streetAddress2} />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::city" text>City</CMS>
                                    </label>
                                    <input
                                        value={model.address.city}
                                        onChange={(e) => setModelWithEffect("address", "city", e.currentTarget?.value)}
                                        name="address_city"
                                        use:validate={[required, (el: any) => maxLength(el, 30)]}
                                        class="form-control" />
                                    <ErrorMessage error={errors.address_city} />
                                </div>
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::state-province" text>State/Province</CMS>
                                    </label>
                                    <input
                                        name="address_state"
                                        value={model.address.state}
                                        onChange={(e) => setModelWithEffect("address","state", e.currentTarget?.value)}
                                        use:validate={[required, (el: any) => maxLength(el, 30)]}
                                        class="form-control" />
                                    <ErrorMessage error={errors.address_state} />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::post-code-zip" text>Post code/ZIP</CMS>
                                    </label>
                                    <input 
                                        value={model.address.postalCode}
                                        onChange={(e) => setModelWithEffect("address", "postalCode", e.currentTarget?.value)}
                                        use:validate={[required, (el: any) => maxLength(el, 20)]}
                                        name="address_postalCode"
                                        class="form-control" />
                                    <ErrorMessage error={errors.address_postalCode} />
                                </div>
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="label::country" text>Country</CMS>
                                    </label>
                                    <select 
                                        value={model.address.countryId}
                                        onChange={(e) => setModelWithEffect("address", "countryId", e.currentTarget.value)}
                                        name="address_countryId"
                                        use:validate={[requiredListItem]}
                                        required
                                        class="form-control">
                                        <For each={countries()}>{(opt) => <option value={opt.Value}>{opt.Text}</option>}</For>
                                    </select>
                                    <ErrorMessage error={errors.address_countryId} />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <label>
                                        <Required /> <CMS id="18th-salon-application/step-1::label/phone-number" text>Phone number (Please include country code where appropriate)</CMS>
                                    </label>
                                    <div class="row">
                                        <div class="col-md-6 col-xs-12">
                                            <input
                                                type="tel"
                                                value={model.contact.phoneNumber}
                                                onChange={(e) => setModelWithEffect("contact", "phoneNumber", e.currentTarget?.value)}
                                                use:validate={[required, (el: any) => maxLength(el, 50)]} 
                                                name="contact_phoneNumber"
                                                class="form-control"
                                            />
                                            <ErrorMessage error={errors.contact_phoneNumber} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr/>
                            <CMS id="18th-salon-application/step-1::section/social-media">
                                <h3 class="m-t-lg">
                                    Social media accounts
                                </h3>
                                <p>
                                    We will use this information to find you on social media so that if we post about your work, we can tag you appropriately. This will assist us in our promotion of you and your work.
                                </p>
                            </CMS>
                            <div class="row">
                                <div class="col-md-4">
                                    <label>
                                        <CMS id="label::facebook" text>Facebook</CMS>
                                    </label>
                                    <input
                                        value={model.socials.facebook}
                                        onChange={(e) => setModelWithEffect("socials","facebook", e.currentTarget?.value)}
                                        class="form-control" />
                                </div>
                                <div class="col-md-4">
                                    <label>
                                        <CMS id="label::instagram" text>Instagram</CMS>
                                    </label>
                                    <input 
                                        value={model.socials.instagram}
                                        onChange={(e) => setModelWithEffect("socials","instagram", e.currentTarget?.value)}
                                        class="form-control" />
                                </div>
                                <div class="col-md-4">
                                    <label>
                                        <CMS id="label::twitter" text>Twitter</CMS>
                                    </label>
                                    <input
                                        value={model.socials.twitter}
                                        onChange={(e) => setModelWithEffect("socials","twitter", e.currentTarget?.value)}
                                        class="form-control" />
                                </div>
                            </div>
                            <hr/>
                            <CMS id="18th-salon-application/step-1::section/biography">
                                <h3 class="m-t-lg">
                                    <Required /> Biography and profile picture
                                </h3>
                                <p>
                                    These may be published in ARC Salon-related material if you become a finalist.
                                </p>
                            </CMS>
                            <div class="row">
                                <div class="col-md-6">
                                    <ModelEditor
                                        field="biography" editor="biographyEditor"
                                        label={getText("18th-salon-application/step-1::section/biography/criteria", "Biography (maximum: 500 words)")} />
                                    <Show when={model.biographyEditor?.isEmpty()}>
                                        <div class="error-message">
                                            Please provide a short biography.
                                        </div>
                                    </Show>
                                </div>
                                <div class="col-md-6">
                                    <CMS id="18th-salon-application/step-1::section/profile-picture">
                                        <label>
                                            Profile picture
                                        </label>
                                        <p>
                                            Images should be in JPEG format and less than 5MBs in size.
                                        </p>
                                        <p>
                                            An image can be uploaded by either clicking the <span class="fa fa-upload" style="font-size: 1.5em; margin: 0 10px;"></span> icon or by dragging an image from your computer into the space below.
                                        </p>
                                    </CMS>
                                    <Show when={state.showImageCropper}>
                                        <div>
                                            <ImageCropper
                                                src={profileImageUrl()} maxHeight={4000} maxWidth={4000}
                                                setInstance={setCropper}
                                            />
                                            <Show when={!!profileImageUrl()}>
                                                <button class="btn btn-primary" style="width: 100%;" onClick={onKeepImageClicked}>Keep Original Image</button>
                                            </Show>
                                        </div>
                                    </Show>
                                    <Show when={!state.showImageCropper}>
                                        <div style="text-align: center;">
                                            <Img src={profileImageUrl()} mode="crop" width="400" />
                                            <button class="btn btn-success" style="width: 100%;" onClick={onChangeImageClicked}>Upload Another Image</button>
                                        </div>
                                    </Show>
                                </div>
                            </div>
                            <div class="row m-t-xxl">
                                <div class="col-md-6">
                                    <CMS id="18th-salon-application/step-1::section/catalogues">
                                        <h3>
                                            ARC Salon&trade; Catalogues
                                        </h3>
                                        <p>
                                            As the cost of one catalogue is already included in the basic entry fee, you only need to pay the shipping to get this copy.
                                        </p>
                                        <p>
                                            You can also pre-order additional ARC Salon&trade; Catalogues for $45.00 each plus shipping.
                                        </p>
                                    </CMS>
                                    <div class="row">
                                        <div class="col-md-12">
                                            <div class="checkbox">
                                                <Form.Group class="mb-3" controlId="want-free-catalogue">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={model.catalogue.wantsFree}
                                                        onChange={() => {
                                                            setModelWithEffect("catalogue", "wantsFree", !model.catalogue.wantsFree);
                                                            if (!model.catalogue.wantsFree) {
                                                                setModelWithEffect("catalogue", "wantsAdditional", false);
                                                                setModelWithEffect("catalogue", "additionalOrdered", 0);
                                                            }
                                                        }}
                                                        label={<CMS id="18th-salon-application/step-1::label/want-free-catalogue">I want my free copy of the ARC Salon Catalogue, and will pay for postage</CMS>}
                                                    />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <div class="row m-t-sm">
                                            <div class="col-md-12">
                                                <Show when={model.catalogue.wantsFree}>
                                                    <div class="checkbox">
                                                        <Form.Group class="mb-3" controlId="pre-order-additional-copies">
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={model.catalogue.wantsAdditional}
                                                                onChange={() => {
                                                                    setModelWithEffect("catalogue", "additionalOrdered", 1);
                                                                    setModelWithEffect("catalogue", "wantsAdditional", !model.catalogue.wantsAdditional);
                                                                }}
                                                                label={
                                                                    <CMS id="18th-salon-application/step-1::label/pre-order-additional-copies">
                                                                        I would like to pre-order additional copies at $45.00 each plus shipping.
                                                                    </CMS>
                                                                }
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                </Show>
                                                <Show when={showWantsAdditionalCataloguesPanel()}>
                                                    <div>
                                                        <p style="margin-top: 20px;">
                                                            <input
                                                                type="number"
                                                                max={2}
                                                                class="form-control"
                                                                style="width: 100px;"
                                                                name="catalogue_additionalOrdered"
                                                                use:validate={[({value}:any) => conditionallyRequired(model.catalogue.wantsAdditional, value)]}
                                                                value={model.catalogue.additionalOrdered}
                                                                onChange={(e) => setModelWithEffect("catalogue", "additionalOrdered", parseInt(e.currentTarget?.value))}
                                                            />
                                                            <CMS id="18th-salon-application/step-1::label/maximum-catalogues">(up to a maximum of 2 additional catalogues)</CMS>
                                                        </p>
                                                        <ErrorMessage error={errors.catalogue_additionalOrdered} />
                                                    </div>
                                                </Show>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="row">
                                        <div class="col-md-6" style="padding-left: 50px;">
                                            <div class="books">
                                                <div class="book">
                                                    <a target="_blank" href="http://www.arc-store.com/16inarcsabo.html">
                                                        <Img
                                                            src={"/ContentSection/12/Images/aa2d8580-745b-4378-8028-912abfc3ccc0.jpg"}
                                                            width={300}
                                                            alt="Example catalogue from the 16th Annual Salon"
                                                        />
                                                    </a>
                                                </div>
                                            </div>
                                            <p style="margin-top: 20px;">
                                                <CMS id="18th-salon-application/step-1::label/example-catalogue">
                                                    Example catalogue from the 16th Annual Salon.
                                                </CMS>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <CMS id="18th-salon-application/step-1::section/rules">
                                <h4 class="salon-heading-md m-t-xxl m-b-lg">
                                    Salon Competition Rules
                                </h4>
                                <div class="row">
                                    <div class="col-md-12">
                                        <p>
                                            All entries must be submitted by <strong>June 13th, 2024 11:59 PM (Pacific Standard Time)</strong>. Artists may enter and win in one or multiple categories. The competition is open to all those who wish to apply worldwide. There is no limit to the number of artworks you may enter. Works do NOT need to be available for sale. Works do NOT need to be available for the live exhibition to compete and win. One copy of the International ARC Salon Book is included with your first entry (shipping for the catalog must be paid at checkout to receive your free copy). The exception to this is student works being submitted in the Teens Category. These entries do not come with a free copy of the ARC Salon Catalog. Copies may be pre-purchased now at a special pre-order price.</p>
                                        <p>
                                            Artists may enter and win in multiple categories; there is no limit to the number of works you may enter. Artists must submit digital files in JPEG format via our online submission system. Glass slides, images on CDs and large format transparencies will not be accepted. All digital images must be at least 1 MB in size. If you have a technical problem submitting your entry, please contact our Webmaster, Iian Neill, for assistance at <a class="tape" href="mailto:iian.d.neill@gmail.com">iian.d.neill@gmail.com</a>.</p>
                                        <p>
                                            The competition is open to all those who wish to apply worldwide. All entries must be original artwork, conceived and created by the entrant within the past 3 years. Representational work in all styles spanning from highly realistic to imaginative qualify. Acceptable media include: oil, watercolor, acrylic, pastel, sculpture, and all traditional mediums. A fuller description of mediums can be found on the <a class="tape" href="https://www.artrenewal.org/SalonApplication/Salon/Home#/rules" target="_blank">Rules</a> page of the prospectus. At this time, we do not accept etchings, photography, or other forms of prints whatsoever, nor do we accept digital art. Painted over prints are strictly prohibited. ARC reserves the right to eliminate works if it feels the method of creation is in question. If the work is in question, we will always do our best to contact and discuss the technique used with the artist before any disqualifications are made.</p>
                                        <p>
                                            By submitting your entry, you grant irrevocable permission (limited-copyrights) to the Art Renewal Center&trade; to reproduce your artwork entered into the ARC Salon on the ARC website and in promotional materials, including but not limited to the ARC Salon Book, DVDs, articles and advertisements promoting ARC and/or the ARC Salon. This is necessary for us to be able to include your work on our website, in our ARC Salon Book and for the general promotion of your work through your participation in the competition. Images will not be used for other commercial purposes and artists retain general copyrights for all other purposes. By submitting your entry, you warrant that your entries are original work; conceived and created entirely by you, that you have read and understand the rules set forth in the competition prospectus, that you are 18 years of age or older, or if under 18, have the permission of a parent or guardian to submit your works and that the parent or guardian is agreeing to the rules set forth in this competition prospectus on minor's behalf. The ARC's <a class="tape" href="https://www.artrenewal.org/Home/TermsAndConditions" target="_blank">Terms and Conditions</a> also apply. Please review our <a class="tape" href="https://www.artrenewal.org/Home/PrivacyPolicy" target="_blank">Privacy Policy</a>.</p>
                                        <p>
                                            To learn more about the jurors of the competition and for more detailed information about entries, <a class="tape" href="https://www.artrenewal.org/SalonApplication/Salon/Home#/awards" target="_blank">awards</a> and <a class="tape" href="https://www.artrenewal.org/SalonApplication/Salon/Home#/rules" target="_blank">rules</a> please return to the pages of the prospectus.
                                        </p>
                                    </div>
                                </div>
                            </CMS>
                            <div class="row">
                                <div class="col-md-12">
                                    <CMS id="18th-salon-application/step-1::section/for-students">
                                        <h3 class="m-t-lg">
                                            For Students
                                        </h3>
                                        <p>
                                            This category is devoted to young aspiring artists between 13 &ndash; 19 years of age. Works created during this age range are also acceptable, so long as the artist is no older then 20 years of age at the time of entry. The Teens Category is intended as a separate competition for students in this age range. These works competing in the Teens Category do not compete in the other ARC Salon categories or for the other awards associated to the rest of the competition. We do however invite the top winners of the Teens Category to participate in the ARC Salon Exhibition and award 1st, 2nd, and 3rd place cash award prizes as well as eight honorable mentions. Please note that in the Teens Category, we do also publish the name of the entrant&rsquo;s school and the name of their primary art instructor. If you have questions or concerns about this please contact <a class="tape" href="mailto:kara.ross@artrenewal.org">kara.ross@artrenewal.org</a>. If an individual within this age range wishes to compete in the other categories of the ARC Salon, they may do so, but should enter as a regular artist and not as a student.
                                        </p>
                                    </CMS>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-12">
                                    <div class="row">
                                        <div class="col-md-12">
                                            <div class="checkbox">
                                                <Form.Group class="mb-3" controlId="checkbox-minimum-age">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={model.isStudent}
                                                        onChange={(e) => setModelWithEffect("isStudent", !model.isStudent)}
                                                        label={
                                                            <CMS id="18th-salon-application/step-1::label/checkbox-minimum-age">
                                                                I am submitting artwork that was created at 13-19 years of age and has been created in the last three years.
                                                            </CMS>
                                                        }
                                                    />
                                                </Form.Group>
                                                <CMS id="18th-salon-application/step-1::section/high-school-student">
                                                    <p style="margin-top: 10px;">
                                                        Entry fee for high school students is <b>$28</b>.<br />
                                                        Students at <A class="tape" target="_blank" href="http://www2.ed.gov/programs/titleiparta/index.html">Title 1</A> schools, if this fee would be a barrier to entry, please write to <a class="tape" href="mailto:yvette.lytle@artrenewal.org">yvette.lytle@artrenewal.org</a>.
                                                    </p>
                                                </CMS>
                                            </div>
                                        </div>
                                    </div>
                                    <Show when={model.isStudent}>
                                        <div>
                                            <hr/ >
                                            <h3>
                                                <CMS text id="18th-salon-application/step-1::heading/student-details">Student details</CMS>
                                            </h3>
                                            <div class="row m-t-sm">
                                                <div class="col-md-6">
                                                    <label>
                                                        <Required /> <CMS id="label::age" text>Age</CMS>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        class="form-control"
                                                        value={model.student?.age} min={13} max={19}
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value)]}
                                                        name="student_age"
                                                        style="width: 100px;"
                                                        onChange={(e) => setModelWithEffect("student", "age", parseInt(e.currentTarget?.value))} />
                                                    <ErrorMessage error={errors.student_age} />
                                                </div>
                                            </div>
                                            <h3>
                                                <CMS id="18th-salon-application/step-1::parent-or-legal-guardian/heading" text>Parent or Legal Guardian Contact Details</CMS>
                                            </h3>
                                            <div class="row m-t-sm">
                                                <div class="col-md-6">
                                                    <label>
                                                        <Required /> <CMS id="label::name">Name</CMS>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        value={model.student?.parentOrLegalGuardian?.name}
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value)]}
                                                        name="student_parentOrLegalGuardian_name"
                                                        onChange={(e) => setModelWithEffect("student", "parentOrLegalGuardian", "name", e.currentTarget?.value)} />
                                                    <ErrorMessage error={errors.student_parentOrLegalGuardian_name} />
                                                </div>
                                            </div>
                                            <div class="row m-t-sm">
                                                <div class="col-md-6">
                                                    <label>
                                                        <Required /> <CMS id="label::email" text>Email</CMS>
                                                    </label>
                                                    <input
                                                        type="email"
                                                        class="form-control"
                                                        value={model.student?.parentOrLegalGuardian?.email}
                                                        onChange={(e) => setModelWithEffect("student", "parentOrLegalGuardian", "email", e.currentTarget?.value)}
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value)]}
                                                        name="student_parentOrLegalGuardian_email"
                                                        />
                                                    <ErrorMessage error={errors.student_parentOrLegalGuardian_email} />
                                                </div>
                                                <div class="col-md-6">
                                                    <label>
                                                        <CMS id="label::phone-number" text>Phone number</CMS>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        value={model.student?.parentOrLegalGuardian?.phoneNumber}
                                                        onChange={(e) => setModelWithEffect("student", "parentOrLegalGuardian", "phoneNumber", e.currentTarget?.value)}
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value)]}
                                                        name="student_parentOrLegalGuardian_phoneNumber"
                                                    />
                                                    <ErrorMessage error={errors.student_parentOrLegalGuardian_phoneNumber} />
                                                </div>
                                            </div>
                                            
                                            <div class="row m-t-sm">
                                                <div class="col-md-6">
                                                    <label>
                                                        <CMS id="label::high-school" text>High school</CMS>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        value={model.student?.school?.name}
                                                        name="student_school_name"
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value), (el: any) => maxLength(el, 100)]}
                                                        onChange={(e) => setModelWithEffect("student", "school", "name", e.currentTarget?.value)}
                                                    />
                                                    <ErrorMessage error={errors.student_school_name} />
                                                </div>
                                            </div>
                                            <div class="row m-t-sm">
                                                <div class="col-md-6">
                                                    <label>
                                                        <CMS id="label::high-school-art-instructor" text>High school art instructor</CMS>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        class="form-control"
                                                        name="student_school_instructors"
                                                        value={model.student?.school?.instructors}
                                                        use:validate={[({value}:any) => conditionallyRequired(model.isStudent, value), (el: any) => maxLength(el, 200)]}
                                                        onChange={(e) => setModelWithEffect("student", "school", "instructors", e.currentTarget?.value)} />
                                                    <ErrorMessage error={errors.student_school_instructors} />
                                                </div>
                                            </div>
                                        </div>
                                    </Show>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div class="row">
                        <div class="col-md-12">
                            <div class="checkbox">
                                <Form.Group class="mb-3" controlId="agreed-to-terms-and-conditions">
                                    <Form.Check
                                        type="checkbox"
                                        checked={model.acceptedTermsAndConditions}
                                        use:validate={[mustAgreeToTermsAndConditions]}
                                        name="acceptedTermsAndConditions"
                                        onChange={(e) => setModelWithEffect("acceptedTermsAndConditions", !model.acceptedTermsAndConditions)}
                                        label={
                                            <CMS id="18th-salon-application/step-1::label/agreed-to-terms-and-conditions">
                                                I have read and agree to the ARC Salon Rules and ARC's <A class="tape" target="_blank" href="https://www.artrenewal.org/SalonApplication/Home/TermsAndConditions">Terms and Conditions</A>.
                                            </CMS>
                                        }
                                    />
                                </Form.Group>
                                {/* <label>
                                    <input
                                        type="checkbox"
                                        class="checks"
                                        checked={model.acceptedTermsAndConditions}
                                        use:validate={[mustAgreeToTermsAndConditions]}
                                        name="acceptedTermsAndConditions"
                                        onChange={(e) => setModel("acceptedTermsAndConditions", !model.acceptedTermsAndConditions)} />
                                    <CMS id="18th-salon-application/step-1::label/agreed-to-terms-and-conditions">
                                        I have read and agree to the ARC Salon Rules and ARC's <A class="tape" target="_blank" href="https://www.artrenewal.org/SalonApplication/Home/TermsAndConditions">Terms and Conditions</A>.
                                    </CMS>
                                </label> */}
                            </div>
                            <ErrorMessage error={errors.acceptedTermsAndConditions} />
                        </div>
                    </div>
                    <div class="row m-t-xxl m-b-lg">
                        <div class="col-md-12">
                            <div id="submit-row">
                                <Button variant="primary" classList={{ ["disabled"]: state.isSubmitting }} size="lg" type="submit">
                                    <Switch>
                                        <Match when={!!model.id}>
                                            <CMS id="button::continue" text>Continue</CMS>
                                        </Match>
                                        <Match when={!model.id}>
                                            <CMS id="button::yes-sign-me-up" text>Yes, sign me up!</CMS>
                                        </Match>
                                    </Switch>
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Show>
    );
};